/**
 * AuditExportModal - Export audit logs in various formats
 *
 * Features:
 * - Format selection (CSV, JSON)
 * - Current filters applied indicator
 * - Download progress feedback
 * - Success/error states
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import { Button } from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@respectly/ui';
import { Label } from '@respectly/ui';
import { RadioGroup, RadioGroupItem } from '@respectly/ui';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  FileSpreadsheet,
  FileJson,
  Filter,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import type { AuditLogFilters } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AuditExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'csv' | 'json') => Promise<string | null>;
  filters: AuditLogFilters;
  activeFiltersCount: number;
  isExporting?: boolean;
}

type ExportFormat = 'csv' | 'json';
type ExportState = 'idle' | 'exporting' | 'success' | 'error';

// ============================================
// Component
// ============================================

export function AuditExportModal({
  isOpen,
  onClose,
  onExport,
  filters: _filters,
  activeFiltersCount,
  isExporting = false,
}: AuditExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [exportState, setExportState] = useState<ExportState>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setExportState('exporting');
    setError(null);

    try {
      const url = await onExport(format);
      if (url) {
        setExportState('success');
        // Auto-trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setExportState('error');
        setError('Failed to generate export file');
      }
    } catch (err) {
      setExportState('error');
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const handleClose = () => {
    setExportState('idle');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-500" />
            Export Audit Logs
          </DialogTitle>
          <DialogDescription>
            Download audit logs in your preferred format.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup
              value={format}
              onValueChange={(v) => setFormat(v as ExportFormat)}
              className="grid grid-cols-2 gap-3"
              disabled={exportState === 'exporting'}
            >
              <Label
                htmlFor="format-csv"
                className={cn(
                  'flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                  format === 'csv'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <RadioGroupItem value="csv" id="format-csv" className="sr-only" />
                <div
                  className={cn(
                    'h-10 w-10 rounded-lg flex items-center justify-center',
                    format === 'csv'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-800'
                  )}
                >
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">CSV</p>
                  <p className="text-xs text-slate-500">Spreadsheet format</p>
                </div>
              </Label>

              <Label
                htmlFor="format-json"
                className={cn(
                  'flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                  format === 'json'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <RadioGroupItem value="json" id="format-json" className="sr-only" />
                <div
                  className={cn(
                    'h-10 w-10 rounded-lg flex items-center justify-center',
                    format === 'json'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-800'
                  )}
                >
                  <FileJson className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">JSON</p>
                  <p className="text-xs text-slate-500">Structured data</p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Filters Notice */}
          {activeFiltersCount > 0 && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30">
              <Filter className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Filters Applied
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                  {activeFiltersCount} active filter{activeFiltersCount > 1 ? 's' : ''} will be
                  applied to the export.
                </p>
              </div>
            </div>
          )}

          {/* Export State Feedback */}
          <AnimatePresence mode="wait">
            {exportState === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30"
              >
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                    Export Complete
                  </p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">
                    Your download should start automatically.
                  </p>
                </div>
              </motion.div>
            )}

            {exportState === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30"
              >
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    Export Failed
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    {error || 'Please try again.'}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            {exportState === 'success' ? 'Close' : 'Cancel'}
          </Button>
          {exportState !== 'success' && (
            <Button
              onClick={handleExport}
              disabled={exportState === 'exporting' || isExporting}
            >
              {exportState === 'exporting' || isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export {format.toUpperCase()}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
